import { createSelector } from '@reduxjs/toolkit';
import { meetingsNotDeleted } from './meetings-not-deleted';
import { employeeInterviewUuid } from './employee-interview-uuid';
import { meetingOccurrencesNotDeleted } from './meeting-occurrences-not-deleted';
import { employeesNotDeleted } from '../employees/employees-not-deleted';
import { currentUserUuid } from '../user/currentUserUuid';
import { reportsNotDeleted } from '../documents/reports-not-deleted';
import { employeesShortNamesAndI } from '../employees/employees-short-names-and-i';
import type { MeetingModel } from './meeting-model';
import { LocalDate } from '../../../utilities/local-date';
import { toMeetingOccurrenceModel } from './to-meeting-occurrence-model';
import { capitalize, entityNameForLists, isEmptyOrInvalidString } from '../../../utilities/text';
import { isEmployeeTerminated } from './is-employee-terminated';
import { sortBy } from 'lodash';
import { getContactsAsUsers } from '../partners/get-contacts-as-users';
import { functionsNotDeleted } from '../responsibilities/functions-not-deleted';
import type { EmployeeViewModelWithName } from 'src/store/types';
import type { PersonAsMessageRecipient } from 'src/content/event-occurrences/person-as-message-recipient';
import type { PartnerContactAsUser, NameEntry } from 'src/store/selectors';

export const meetings = createSelector(
  meetingsNotDeleted,
  employeeInterviewUuid,
  meetingOccurrencesNotDeleted,
  functionsNotDeleted,
  employeesNotDeleted,
  currentUserUuid,
  reportsNotDeleted,
  employeesShortNamesAndI,
  getContactsAsUsers,
  function (
    meetings,
    employeeInterviewUuid,
    occurrences,
    functions,
    employees,
    currentUserId,
    reports,
    employeesShortNamesAndI,
    contactsAsUsers,
  ) {
    let groups: MeetingModel[] = [];
    const today = LocalDate.now();
    // Create groups for all meeting occurrences except employee interviews
    occurrences
      .filter((occurrence) => {
        return occurrence.name !== 'Medarbeidersamtale';
      })
      .forEach((occurrence) => {
        let future = false;
        if (LocalDate.fromString(occurrence.date).isSameOrAfter(today)) {
          future = true;
        }
        const existingGroup = groups.find((group) => {
          return group.name === occurrence.name;
        });
        if (existingGroup) {
          groups = groups.map((group) => {
            if (group.name === occurrence.name) {
              if (future) {
                group.type = 'FUTURE';
              }
              group.meetingOccurrences.push(
                toMeetingOccurrenceModel(
                  occurrence,
                  currentUserId ?? '',
                  reports,
                  functions,
                  employeesShortNamesAndI,
                  contactsAsUsers,
                ),
              );
            }
            return group;
          });
        } else {
          let uuid = occurrence.uuid;
          let meetingUuid: string | undefined = undefined;
          const meeting = meetings.find((meeting) => {
            return meeting.name === occurrence.name;
          });
          if (meeting) {
            uuid = meeting.uuid;
            meetingUuid = meeting.uuid;
          }
          groups.push({
            uuid,
            name: entityNameForLists(occurrence.name, occurrence.draftName, occurrence.isConfirmedEntity),
            interviewEmployeeName: '',
            interviewEmployeeUuid: '',
            type: future ? 'FUTURE' : 'PAST',
            meetingOccurrences: [
              toMeetingOccurrenceModel(
                occurrence,
                currentUserId ?? '',
                reports,
                functions,
                employeesShortNamesAndI,
                contactsAsUsers,
              ),
            ],
            meetingUuid: meetingUuid,
          });
        }
      });
    // Create interview groups for all employees
    employees
      .filter((employee) => {
        return employee.isConfirmedEntity;
      })
      .forEach((employee) => {
        const interviewOccurrences = occurrences
          .filter((occurrence) => {
            return occurrence.name === 'Medarbeidersamtale' && occurrence.participants.includes(employee.uuid);
          })
          .map((occurrence) => {
            return toMeetingOccurrenceModel(
              occurrence,
              currentUserId ?? '',
              reports,
              functions,
              employeesShortNamesAndI,
              contactsAsUsers,
            );
          });
        groups.push({
          uuid: employee.uuid,
          name: 'Medarbeidersamtale',
          interviewEmployeeName: employee.name,
          interviewEmployeeUuid: employee.uuid,
          type: isEmployeeTerminated(employee.uuid, employees) ? 'INTERVIEW_TERMINATED' : 'INTERVIEW_ACTIVE',
          meetingOccurrences: interviewOccurrences,
        });
      });
    // Create groups for all template meetings with no occurrences
    meetings.forEach((meeting) => {
      const existingGroup = groups.find((g) => {
        return g.name === meeting.name;
      });
      if (!existingGroup && meeting.uuid !== employeeInterviewUuid) {
        groups.push({
          uuid: meeting.uuid,
          name: meeting.name || '',
          interviewEmployeeName: '',
          interviewEmployeeUuid: '',
          type: 'SUGGESTED',
          meetingOccurrences: [],
          meetingUuid: meeting.uuid,
        });
      }
    });
    return groups.map((group) => {
      group.meetingOccurrences = sortBy(group.meetingOccurrences, ['dateTimeForSorting']);
      return group;
    });
  },
);

export function availableMeetingMessageRecipients(
  employeesShortNamesAndI: NameEntry[],
  employeesNotDeleted: EmployeeViewModelWithName[],
  contactsAsUsers: PartnerContactAsUser[],
  participants: string[],
  externalParticipants: string[],
  isEmployeeInterview: boolean,
): PersonAsMessageRecipient[] {
  const employees: PersonAsMessageRecipient[] = sortBy(
    employeesShortNamesAndI
      .filter((e) => e.name !== '')
      .filter((e) => e.status !== 'TERMINATED' || participants.includes(e.uuid))
      .map((p) => {
        const employee = employeesNotDeleted.find((item) => item.uuid === p.uuid);
        return {
          type: 'employee',
          participant: participants.includes(p.uuid),
          uuid: p.uuid,
          name: capitalize(p.name),
          email: employee?.email ?? '',
          accessLevel: employee?.accessLevel ?? 'NONE',
        };
      }),
    ['text'],
  );
  const contacts: PersonAsMessageRecipient[] = sortBy(
    contactsAsUsers
      .filter((p) => {
        return !isEmptyOrInvalidString(p.name);
      })
      .map((p) => {
        return {
          type: 'contact',
          participant: externalParticipants.includes(p.uuid),
          uuid: p.uuid,
          name: p.name.trim() + ' (' + p.partnerName + ')',
          email: p.email ?? '',
          accessLevel: p.accessLevel ?? 'NONE',
        };
      }),
    ['text'],
  );
  const result = sortBy(employees.concat(contacts), [
    (p) => (p.participant ? 0 : 1),
    (p) => (p.email === '' ? 1 : 0),
    (p) => (p.accessLevel === 'NONE' ? 1 : 0),
    (p) => p.name,
  ]);
  if (isEmployeeInterview) {
    return result.filter((r) => r.participant);
  }
  return result;
}

export function meetingParticipants(
  meetingResponsibleFunctionEmployeeUuid: string | undefined,
  responsibleEmployeeUuid: string | undefined,
  selectedEmployees: string[] = [],
  selectedContacts: string[] = [],
): string[] {
  const result: string[] = selectedEmployees.concat(selectedContacts);
  if (meetingResponsibleFunctionEmployeeUuid) {
    result.push(meetingResponsibleFunctionEmployeeUuid);
  }
  if (responsibleEmployeeUuid) {
    result.push(responsibleEmployeeUuid);
  }
  return result;
}
